import { StatusItem } from "@types";
import { useState, useEffect } from "react";
import { Typography, Button, Box } from "@mui/material";
import { ImageIcon } from "common-lib";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useTutorial } from "../../../context/TutorialContext";
import { addReviewDataSet } from "../../../services/datasetService";
import { TUTORIALS } from "../../../tutorial";
import { convertEditDataSet } from "../../../utils/datasetFileUtil";
import { transFiles, SF } from "../../../utils/utils";
import { v4 as uuidv4 } from "uuid";
import { t } from "i18next";
import {
  getNGLabels,
  updateReviewLabel,
  getOKRandomLabels,
  getInspStatusImage,
} from "../../../services/inspectService";
import { FilterState } from "../../../@types/services/inspStatusService";

// review: {"label_seg":[...], "label_cls":["NG"]} 에서 label_seg만 추출. 없거나 파싱 실패면 []
const parseLabelSeg = (review: string | null): unknown[] => {
  if (!review) return [];
  try {
    const segs = JSON.parse(review).label_seg;
    return Array.isArray(segs) ? segs : [];
  } catch {
    return [];
  }
};

interface MakeDataSetContentProps {
  filters: FilterState;
  totalDataCount: number;
  onClose: () => void;
  onPrevious: () => void;
}

export default function MakeDataSetContent({ onClose, onPrevious, filters, totalDataCount }: MakeDataSetContentProps) {
  const navigate = useNavigate();
  const [ngList, setNgList] = useState<StatusItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<StatusItem | null>(null);
  const [okCount, setOkCount] = useState(0); // 사용자가 지정한, 데이터셋에 넣을 OK 이미지 수
  const [maxOk, setMaxOk] = useState(0); // 필터 조건 내 전체 OK 이미지 수 (입력 상한)
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [zoom, setZoom] = useState(false);
  const nowDate = new Date().toLocaleString("ko-KR", {
    timeZone: "Asia/Seoul",
  });
  const randomId = uuidv4().replace(/-/g, "").slice(0, 12);
  const dataSetFormat = {
    class_list: ["OK", "NG"],
    datasetType: "stsup-cls",
    description: `auto-generated dataset ${nowDate}`,
    files: [],
    labeled_count: [],
    material: `${filters.material || ""}`,
    name: `review_dataset_${randomId}`,
    path: "",
    type: "stsup-cls",
  };

  const { run, tutorialKey, stepIndexRef, nextSubStep } = useTutorial();

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const response = await getNGLabels({ ...filters });
        const data: StatusItem[] = response.data;
        console.log(response);
        setNgList(data);
        setTotalCount(totalDataCount);
        const initialOk = totalDataCount ? totalDataCount - data.length : 0;
        setMaxOk(initialOk);
        setOkCount(initialOk);
        setSelectedItem(data[0] ?? null);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (ngList.length === 0 || !selectedItem) return;

      const currentIndex = ngList.findIndex((i) => i.id === selectedItem.id);

      if (e.key === "ArrowDown") {
        e.preventDefault(); // 스크롤 방지
        const nextItem = ngList[currentIndex + 1];
        if (nextItem) handleRowClick(nextItem);
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        const prevItem = ngList[currentIndex - 1];
        if (prevItem) handleRowClick(prevItem);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedItem, ngList]);

  const handleRemove = () => {
    if (!selectedItem) return;
    setNgList((prev) => {
      const next = prev.filter((i) => i.id !== selectedItem.id);
      setSelectedItem(next[0] ?? null);
      return next;
    });
  };

  const handleChangeToOk = async () => {
    if (!selectedItem) return;

    await updateReviewLabel(selectedItem.uuid, "OK");

    setNgList((prev) => {
      const next = prev.filter((i) => i.id !== selectedItem.id);
      const removedIndex = prev.findIndex((i) => i.id === selectedItem.id);
      setSelectedItem(next[removedIndex] ?? next[removedIndex - 1] ?? null);
      return next;
    });

    setMaxOk((m) => m + 1);
    setOkCount((c) => (c === maxOk ? c + 1 : c));
  };

  const handleApply = async () => {
    try {
      const randomOkData = okCount > 0 ? ((await getOKRandomLabels(filters, okCount))?.data ?? []) : [];

      const okData = convertEditDataSet(randomOkData).map((item: { label?: string }) => ({ ...item, label: "OK" }));
      const combinedData = [...convertEditDataSet(ngList), ...okData];

      // review.label_seg(컨투어)가 있는 NG 이미지만 path별로 모아 전송 → 데이터셋 파일 항목에 컨투어로 기록됨
      const contours: Record<string, unknown[]> = {};
      for (const item of ngList) {
        const segs = parseLabelSeg(item.review);
        if (segs.length) contours[item.path] = segs;
      }

      await addReviewDataSet(
        {
          ...dataSetFormat,
          files: transFiles(combinedData),
        },
        contours,
      );
      toast.success(t("DatasetCreatedMsg"));
      if (run) {
        navigate("/main/projects");
        const currentSubStep = TUTORIALS[tutorialKey][stepIndexRef.current];
        if (currentSubStep?.nextTrigger === "#expand-collapse-dataset-btn") {
          nextSubStep();
        }
      }
      onClose();
    } catch (error) {
      console.log(error);
    }
  };

  const handleRowClick = async (item: StatusItem) => {
    setSelectedItem(item);
    const image = await getInspStatusImage(item.path);
    setImageSrc(image ?? null);
  };

  return (
    <Box id="make-dataset-modal" sx={{ height: "80vh", display: "flex", flexDirection: "column" }}>
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* 왼쪽 리스트 */}
        <Box
          id="review-ng-list"
          sx={{
            width: 260,
            flexShrink: 0,
            borderRight: "0.5px solid var(--dk-border, rgba(0,0,0,0.08))",
            display: "flex",
            flexDirection: "column",
            background: "var(--dk-surface, white)",
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-thumb": { background: "rgba(0,0,0,0.12)", borderRadius: "99px" },
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                <Typography sx={{ fontSize: "13px", color: "var(--dk-text-3, #aeaeb2)", fontFamily: SF }}>
                  {t("LoadingMsg")}
                </Typography>
              </Box>
            ) : (
              ngList.map((item) => (
                <Box
                  key={item.id}
                  onClick={() => handleRowClick(item)}
                  sx={{
                    px: "14px",
                    py: "9px",
                    fontSize: "13px",
                    cursor: "pointer",
                    borderBottom: "0.5px solid var(--dk-border, rgba(0,0,0,0.05))",
                    letterSpacing: "-0.01em",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    transition: "background 0.1s",
                    fontFamily: SF,
                    ...(selectedItem?.id === item.id
                      ? { background: "var(--dk-info-bg, #eff6ff)", color: "#2563eb", fontWeight: 600 }
                      : {
                          color: "var(--dk-text-2, #3a3a3c)",
                          "&:hover": { background: "var(--dk-surface-2, #f5f5f7)" },
                        }),
                  }}
                >
                  {item.name}
                </Box>
              ))
            )}
          </Box>

          {/* 리스트 하단 버튼 */}
          <Box
            sx={{
              p: "10px",
              borderTop: "0.5px solid var(--dk-border, rgba(0,0,0,0.08))",
              display: "flex",
              gap: "7px",
              flexShrink: 0,
            }}
          >
            {[
              {
                label: t("Delete"),
                onClick: handleRemove,
                style: { borderColor: "#fca5a5", color: "#dc2626", "&:hover": { background: "#fee2e2" } },
              },
              {
                label: t("ChangeToOk"),
                onClick: handleChangeToOk,
                style: {
                  borderColor: "var(--dk-border, rgba(0,0,0,0.14))",
                  color: "var(--dk-text-2, #3a3a3c)",
                  "&:hover": { background: "var(--dk-surface-2, #f5f5f7)" },
                },
              },
            ].map((btn) => (
              <Button
                key={btn.label}
                onClick={btn.onClick}
                disabled={!selectedItem}
                sx={{
                  flex: 1,
                  height: 30,
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 500,
                  textTransform: "none",
                  fontFamily: SF,
                  border: "0.5px solid",
                  background: "var(--dk-surface, white)",
                  "&.Mui-disabled": { opacity: 0.4 },
                  ...btn.style,
                }}
              >
                {btn.label}
              </Button>
            ))}
          </Box>
        </Box>

        {/* 오른쪽 이미지 */}
        <Box
          sx={{
            flex: 1,
            background: "var(--dk-surface-2, #fafafa)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {selectedItem && imageSrc ? (
            <img
              src={imageSrc}
              alt={selectedItem.name}
              style={{ maxWidth: "100%", maxHeight: "80%", objectFit: "contain" }}
            />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "13px",
                  background: "#f1f1f3",
                  border: "0.5px solid var(--dk-border, rgba(0,0,0,0.08))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ImageIcon size={22} color="#aeaeb2" />
              </Box>
              <Typography
                sx={{ fontSize: "12px", color: "var(--dk-text-3, #aeaeb2)", letterSpacing: "-0.01em", fontFamily: SF }}
              >
                {t("NoImageMsg")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          height: 52,
          borderTop: "0.5px solid var(--dk-border, rgba(0,0,0,0.08))",
          background: "var(--dk-surface-glass, rgba(255,255,255,0.5))",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "16px",
          flexShrink: 0,
        }}
      >
        {/* OK 카운트 */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box
            id="make-dataset-ok-count"
            sx={{
              background: "var(--dk-surface, white)",
              border: "0.5px solid var(--dk-border, rgba(0,0,0,0.1))",
              borderRadius: "9px",
              px: "12px",
              py: "5px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            }}
          >
            <Typography
              sx={{
                fontSize: "10px",
                color: "var(--dk-text-3, #aeaeb2)",
                fontWeight: 500,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                display: "block",
                mb: "1px",
                fontFamily: SF,
              }}
            >
              {t("OkImageCount")}
            </Typography>
            <input
              type="number"
              min={0}
              max={maxOk}
              value={okCount}
              onChange={(e) => {
                const n = Math.floor(Number(e.target.value));
                setOkCount(Number.isFinite(n) ? Math.min(Math.max(n, 0), maxOk) : 0);
              }}
              style={{
                width: 72,
                fontSize: "14px",
                fontWeight: 700,
                color: "var(--dk-text, #1d1d1f)",
                fontFamily: SF,
                background: "transparent",
                border: "none",
                outline: "none",
                padding: 0,
              }}
            />
          </Box>
          <Typography sx={{ fontSize: "13px", color: "var(--dk-text-3, #aeaeb2)", fontFamily: SF }}>
            / {totalCount}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: "7px", cursor: "pointer" }}
            onClick={() => setZoom((v) => !v)}
          >
            <Box
              sx={{
                width: 36,
                height: 20,
                borderRadius: "99px",
                background: zoom ? "#2563eb" : "#e8e8ed",
                position: "relative",
                transition: "background 0.2s",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "2px",
                  left: zoom ? "18px" : "2px",
                  width: 16,
                  height: 16,
                  background: "var(--dk-surface, white)",
                  borderRadius: "50%",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  transition: "left 0.2s",
                }}
              />
            </Box>
            <Typography sx={{ fontSize: "12px", color: "var(--dk-text-2, #6e6e73)", fontFamily: SF }}>
              {t("Zoom")}
            </Typography>
          </Box>

          <Button
            id="apply-make-dataset-btn"
            onClick={handleApply}
            sx={{
              height: 32,
              px: "18px",
              background: "#2563eb",
              color: "white",
              borderRadius: "9px",
              fontSize: "13px",
              fontWeight: 600,
              textTransform: "none",
              fontFamily: SF,
              boxShadow: "0 1px 4px rgba(37,99,235,0.3)",
              "&:hover": { background: "#1d4ed8" },
            }}
          >
            {t("Apply").toUpperCase()}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
